import {createCssVar} from './createCSSVar';

type Options = {
  prefix?: string;
  applyNamespace?: boolean;
};

/**
 * Converts an array of strings into an object of values and CSS variable tokens.
 * Optionally adds a prefix to the CSS variable name.
 *
 * ## Example
 *
 * ```js
 * const arr = ['a', 'b', 'c'];
 *
 * const cssVars = generateTokensFromArray(arr);
 * ```
 *
 * ### Returns
 * ```js
 * {
 *  a: 'var(--a)',
 *  b: 'var(--b)',
 *  c: 'var(--c)'
 * }
 * ```
 */

export const generateTokensFromArray = <T extends string>(
  arr: Array<T>,
  options: Options = {}
): Record<T, string> => {
  const {prefix, applyNamespace} = options;

  const tokens = arr.reduce(
    (acc, value) => {
      const varName = createCssVar({
        value,
        prefix,
        applyNamespace,
      });

      acc[value] = `var(${varName})`;

      return acc;
    },
    {} as Record<T, string>
  );

  return tokens;
};
